import React from 'react';
import { Container, Typography, Box, Button, Chip, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowForward, StarBorder } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import productsData from '../data/products.json';

const AllProjects = () => {
  const { t } = useTranslation();
  const { mode } = useTheme();

  // Load product data from JSON file
  const products = productsData.products.map(product => ({
    ...product,
    name: t(`products.product${product.id}.name`) || product.name,
    description: t(`products.product${product.id}.desc`) || product.description,
  }));

  const getBadgeColor = (badge) => {
    const colors = {
      Popular: '#ff6b6b',
      New: '#4ecdc4',
      Featured: '#f39c12',
      Premium: '#9b59b6',
      Enterprise: '#3498db',
      Hot: '#e74c3c',
    };
    return colors[badge] || '#667eea';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 10, md: 12 },
        background: (theme) =>
          mode === 'dark'
            ? 'radial-gradient(ellipse at top, #1e293b 0%, #0f172a 100%)'
            : 'radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), ' +
            'radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 8,
            mt: { xs: 8, sm: 6, md: 4 }, // Add responsive margin top for mobile hamburger menu
            opacity: 0,
            animation: 'fadeInUp 1s ease-out forwards',
            '@keyframes fadeInUp': {
              '0%': { opacity: 0, transform: 'translateY(30px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: { xs: '0.875rem', md: '1rem' },
              fontWeight: 800,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 2,
              display: 'block',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('products.header') || 'AI Solution Provider'}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
              fontWeight: 900,
              mb: 2,
              background: (theme) =>
                mode === 'dark'
                  ? 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'
                  : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
            }}
          >
            {t('products.allTitle') || 'All Our Products'}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: (theme) =>
                mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {t('products.allSubtitle') ||
              'Explore our complete range of AI-powered solutions designed to transform your organization'}
          </Typography>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={4} sx={{ position: 'relative' }}>
          {products.map((product, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={product.id}
              sx={{
                opacity: 0,
                animation: 'fadeIn 0.8s ease-out forwards',
                animationDelay: `${index * 0.1}s`,
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: (theme) =>
                    mode === 'dark'
                      ? 'rgba(30, 41, 59, 0.9)'
                      : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: (theme) =>
                    mode === 'dark'
                      ? '1px solid rgba(148, 163, 184, 0.2)'
                      : '1px solid rgba(148, 163, 184, 0.3)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
                  },
                }}
              >
                {/* Badge */}
                <Chip
                  label={product.badge}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 2,
                    backgroundColor: getBadgeColor(product.badge),
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  }}
                />

                {/* Gradient Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '200px',
                    background: product.gradient,
                    opacity: 0.15,
                    zIndex: 0,
                  }}
                />

                {/* Product Image */}
                <CardMedia
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3,
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={product.image}
                    alt={product.name}
                    sx={{
                      maxWidth: '90%',
                      maxHeight: '90%',
                      objectFit: 'contain',
                      transition: 'transform 0.4s ease',
                      filter: 'drop-shadow(0 10px 25px rgba(0, 0, 0, 0.15))',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </CardMedia>

                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: '1.35rem',
                      color: (theme) =>
                        mode === 'dark' ? '#fff' : '#0f172a',
                      lineHeight: 1.3,
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: '0.95rem',
                      color: (theme) =>
                        mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : 'rgba(0, 0, 0, 0.6)',
                      lineHeight: 1.6,
                      flexGrow: 1,
                    }}
                  >
                    {product.description}
                  </Typography>

                  {/* Features */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                    {product.features.map((feature, idx) => (
                      <Chip
                        key={idx}
                        label={feature}
                        size="small"
                        icon={<StarBorder sx={{ fontSize: 14 }} />}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: (theme) =>
                            mode === 'dark'
                              ? 'rgba(99, 102, 241, 0.15)'
                              : 'rgba(99, 102, 241, 0.1)',
                          color: '#667eea',
                          border: '1px solid rgba(99, 102, 241, 0.2)',
                          '& .MuiChip-icon': {
                            color: '#667eea',
                          },
                        }}
                      />
                    ))}
                  </Box>

                  {/* Action Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForward />}
                    component={Link}
                    to={`/detailed-product?product=${product.id}`}
                    sx={{
                      py: 1.2,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      background: product.gradient,
                      boxShadow: 'none',
                      '&:hover': {
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {t('products.learnMore') || 'Learn More'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default AllProjects;

